import { db } from '../firebase'
import {
  loadOrderFinancials,
  frozenCommissionAmount,
} from './orderFinancials'
import { 
  collection, 
  query, 
  where, 
  getDocs,
  doc,
  getDoc,
  Timestamp,
  orderBy,
  limit as firestoreLimit
} from 'firebase/firestore'

export interface SellerPerformance {
  sellerId: string
  sellerName: string
  totalOffers: number
  acceptedOffers: number
  rejectedOffers: number
  pendingOffers: number
  completedOrders: number
  cancelledOrders: number
  averageResponseTime: number // minutes
  averageRating: number
  totalRatings: number
  lostDeals: number
  lostDealsReasons: Record<string, number>
  totalRevenue: number
  totalCommission: number
  acceptanceRate: number // percentage
  completionRate: number // percentage
  performanceScore: number // 0-100
  period: {
    start: Date
    end: Date
  }
}

export interface SellerSummary {
  sellerId: string
  sellerName: string
  totalOffers: number
  acceptanceRate: number
  averageResponseTime: number
  averageRating: number
  totalRevenue: number
  status: 'excellent' | 'good' | 'average' | 'poor'
}

/**
 * Get comprehensive performance data for a specific seller
 */
export async function getSellerPerformance(
  sellerId: string, 
  startDate: Date, 
  endDate: Date
): Promise<SellerPerformance> {
  try {
    const start = Timestamp.fromDate(startDate)
    const end = Timestamp.fromDate(endDate)

    // Get seller info
    const sellerDocRef = doc(db, 'users', sellerId)
    const sellerDoc = await getDoc(sellerDocRef)
    const sellerName = sellerDoc.exists() ? sellerDoc.data()?.name || 'Unknown' : 'Unknown'

    // Get offers data
    const offersQuery = query(
      collection(db, 'offers'),
      where('sellerId', '==', sellerId),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end)
    )
    const offersSnapshot = await getDocs(offersQuery)
    
    let totalOffers = 0
    let acceptedOffers = 0
    let rejectedOffers = 0
    let pendingOffers = 0
    let totalResponseTime = 0
    let responsesCount = 0

    offersSnapshot.forEach(doc => {
      const data = doc.data()
      totalOffers++
      
      if (data.status === 'accepted') acceptedOffers++
      else if (data.status === 'rejected') rejectedOffers++
      else if (data.status === 'pending') pendingOffers++

      // Calculate response time
      if (data.createdAt && data.updatedAt && data.status !== 'pending') {
        const responseTime = (data.updatedAt.toMillis() - data.createdAt.toMillis()) / (1000 * 60)
        totalResponseTime += responseTime
        responsesCount++
      }
    })

    const averageResponseTime = responsesCount > 0 ? totalResponseTime / responsesCount : 0

    // Get completed/cancelled orders from requests with accepted offers
    // Optimized: Get all accepted offers first, then check requests
    const acceptedOffersQuery = query(
      collection(db, 'offers'),
      where('sellerId', '==', sellerId),
      where('status', '==', 'accepted'),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end)
    )
    const acceptedOffersSnapshot = await getDocs(acceptedOffersQuery)
    
    // Map requestId -> offer price
    const acceptedOffersMap: Record<string, number> = {}
    acceptedOffersSnapshot.forEach(doc => {
      const data = doc.data()
      if (data.requestId) {
        acceptedOffersMap[data.requestId] = data.price || 0
      }
    })
    
    // Get requests that have accepted offers from this seller
    const requestIds = Object.keys(acceptedOffersMap)
    let completedOrders = 0
    let cancelledOrders = 0
    let totalRevenue = 0
    // القيم المجمّدة — مصدر العمولة الوحيد
    const financials = await loadOrderFinancials()
    let frozenCommissionTotal = 0
    
    if (requestIds.length > 0) {
      // Get order statuses for this seller in batches
      // The actual order status for seller is stored in 'order_statuses' collection
      const batchSize = 10
      for (let i = 0; i < requestIds.length; i += batchSize) {
        const batch = requestIds.slice(i, i + batchSize)
        
        // Get requests and order statuses in parallel
        const requestsPromises = batch.map(requestId => getDoc(doc(db, 'requests', requestId)))
        const orderStatusPromises = batch.map(requestId => 
          getDoc(doc(db, 'order_statuses', `${requestId}_${sellerId}`))
        )
        
        const [requestsDocs, orderStatusDocs] = await Promise.all([
          Promise.all(requestsPromises),
          Promise.all(orderStatusPromises)
        ])
        
        requestsDocs.forEach((reqDoc, index) => {
          if (reqDoc.exists()) {
            const reqData = reqDoc.data()
            const offerPrice = acceptedOffersMap[reqDoc.id] || 0
            const orderStatusDoc = orderStatusDocs[index]
            
            // Get the actual order status from order_statuses collection for this seller
            // If order_statuses doesn't exist, fallback to request status
            let actualOrderStatus: string | null = null
            let orderStatusUpdatedAt: number | null = null
            
            if (orderStatusDoc && orderStatusDoc.exists()) {
              const orderStatusData = orderStatusDoc.data()
              actualOrderStatus = orderStatusData.status || null
              
              // Get updatedAt from order_statuses
              if (orderStatusData.updatedAt) {
                if (orderStatusData.updatedAt.toMillis) {
                  orderStatusUpdatedAt = orderStatusData.updatedAt.toMillis()
                } else if (orderStatusData.updatedAt instanceof Timestamp) {
                  orderStatusUpdatedAt = orderStatusData.updatedAt.toMillis()
                } else if (typeof orderStatusData.updatedAt === 'number') {
                  orderStatusUpdatedAt = orderStatusData.updatedAt
                } else if (orderStatusData.updatedAt instanceof Date) {
                  orderStatusUpdatedAt = orderStatusData.updatedAt.getTime()
                } else {
                  const parsed = new Date(orderStatusData.updatedAt).getTime()
                  if (!isNaN(parsed)) {
                    orderStatusUpdatedAt = parsed
                  }
                }
              }
            }
            
            // Fallback to request status if order_statuses doesn't exist
            if (!actualOrderStatus) {
              actualOrderStatus = reqData.status || null
            }
            
            // Get updatedAt from request as fallback
            let reqUpdatedAt: number | null = null
            if (reqData.updatedAt) {
              if (reqData.updatedAt.toMillis) {
                reqUpdatedAt = reqData.updatedAt.toMillis()
              } else if (reqData.updatedAt instanceof Timestamp) {
                reqUpdatedAt = reqData.updatedAt.toMillis()
              } else if (typeof reqData.updatedAt === 'number') {
                reqUpdatedAt = reqData.updatedAt
              } else if (reqData.updatedAt instanceof Date) {
                reqUpdatedAt = reqData.updatedAt.getTime()
              } else {
                const parsed = new Date(reqData.updatedAt).getTime()
                if (!isNaN(parsed)) {
                  reqUpdatedAt = parsed
                }
              }
            }
            
            // Use order_statuses updatedAt if available, otherwise use request updatedAt
            const finalUpdatedAt = orderStatusUpdatedAt || reqUpdatedAt
            
            // For completed orders: check if status is 'delivered' (for seller) or 'completed'/'received' (for request)
            // 'delivered' in order_statuses means the order was delivered by the seller
            if (actualOrderStatus === 'delivered' || actualOrderStatus === 'completed' || actualOrderStatus === 'received') {
              // Count if:
              // 1. updatedAt is within date range (order completed/delivered in period), OR
              // 2. updatedAt is missing/null (fallback: count if offer was accepted in period which we already verified)
              if (!finalUpdatedAt || (finalUpdatedAt >= start.toMillis() && finalUpdatedAt <= end.toMillis())) {
                completedOrders++
                totalRevenue += offerPrice
                // العمولة من القيمة المجمّدة لهذا الطلب (لا من فئات التاجر الحالية)
                const frozen = financials.get(reqDoc.id)
                const amount = frozenCommissionAmount(frozen)
                if (amount != null) frozenCommissionTotal += amount
              }
            } else if (actualOrderStatus === 'cancelled') {
              // Count if:
              // 1. updatedAt is within date range (order cancelled in period), OR  
              // 2. updatedAt is missing/null (fallback: count if offer was accepted in period which we already verified)
              if (!finalUpdatedAt || (finalUpdatedAt >= start.toMillis() && finalUpdatedAt <= end.toMillis())) {
                cancelledOrders++
              }
            }
            // Note: Orders with status 'pending', 'confirmed', 'expired' are not counted
            // as they are not completed or cancelled yet
          }
        })
      }
    }

    // Get ratings
    const ratingsQuery = query(
      collection(db, 'ratings'),
      where('sellerId', '==', sellerId),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end)
    )
    const ratingsSnapshot = await getDocs(ratingsQuery)
    
    let totalRatingValue = 0
    let totalRatings = 0
    
    ratingsSnapshot.forEach(doc => {
      const data = doc.data()
      totalRatingValue += data.rating || 0
      totalRatings++
    })

    const averageRating = totalRatings > 0 ? totalRatingValue / totalRatings : 0

    // Get lost deals
    const lostDealsQuery = query(
      collection(db, 'lost_deals'),
      where('sellerId', '==', sellerId),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end)
    )
    const lostDealsSnapshot = await getDocs(lostDealsQuery)
    
    const lostDealsReasons: Record<string, number> = {}
    lostDealsSnapshot.forEach(doc => {
      const data = doc.data()
      const reason = data.reason || 'unknown'
      lostDealsReasons[reason] = (lostDealsReasons[reason] || 0) + 1
    })

    // العمولة = مجموع القيم المجمّدة للطلبات المكتملة فقط.
    // لا نقرأ settings/commission_rates ولا فئات التاجر الحالية، وإلا فإن تعديل
    // النسبة من لوحة التحكم كان سيغيّر أرقام هذا التقرير لكل периодات السابقة.
    const totalCommission = frozenCommissionTotal
    const commissionRate =
      totalRevenue > 0 ? totalCommission / totalRevenue : 0


    // Calculate rates
    const acceptanceRate = totalOffers > 0 ? (acceptedOffers / totalOffers) * 100 : 0
    // Total order attempts = all accepted offers (regardless of final status)
    // This includes completed, cancelled, pending, and confirmed orders
    const totalOrderAttempts = acceptedOffers
    const completionRate = totalOrderAttempts > 0 ? (completedOrders / totalOrderAttempts) * 100 : 0

    // Calculate performance score
    const performanceScore = calculatePerformanceScore({
      acceptanceRate,
      completionRate,
      averageRating,
      averageResponseTime,
      lostDealsCount: lostDealsSnapshot.size
    })

    return {
      sellerId,
      sellerName,
      totalOffers,
      acceptedOffers,
      rejectedOffers,
      pendingOffers,
      completedOrders,
      cancelledOrders,
      averageResponseTime,
      averageRating,
      totalRatings,
      lostDeals: lostDealsSnapshot.size,
      lostDealsReasons,
      totalRevenue,
      totalCommission,
      acceptanceRate,
      completionRate,
      performanceScore,
      period: { start: startDate, end: endDate }
    }
  } catch (error) {
    console.error('Error getting seller performance:', error)
    throw error
  }
}

/**
 * Get performance data for all sellers (optimized version)
 */
export async function getAllSellersPerformance(
  startDate: Date, 
  endDate: Date
): Promise<SellerSummary[]> {
  try {
    const start = Timestamp.fromDate(startDate)
    const end = Timestamp.fromDate(endDate)
    
    // Get all sellers
    const sellersQuery = query(
      collection(db, 'users'),
      where('type', '==', 'seller')
    )
    const sellersSnapshot = await getDocs(sellersQuery)
    
    if (sellersSnapshot.empty) {
      return []
    }
    
    // Get all data in parallel for better performance
    const [allOffersSnapshot, allRatingsSnapshot] = await Promise.all([
      // Get all offers in period
      getDocs(query(
        collection(db, 'offers'),
        where('createdAt', '>=', start),
        where('createdAt', '<=', end)
      )),
      // Get all ratings in period
      getDocs(query(
        collection(db, 'ratings'),
        where('createdAt', '>=', start),
        where('createdAt', '<=', end)
      ))
    ])
    
    // Group offers by sellerId
    const offersBySeller: Record<string, any[]> = {}
    allOffersSnapshot.forEach(doc => {
      const data = doc.data()
      const sellerId = data.sellerId
      if (!offersBySeller[sellerId]) {
        offersBySeller[sellerId] = []
      }
      offersBySeller[sellerId].push(data)
    })
    
    // Group ratings by sellerId
    const ratingsBySeller: Record<string, any[]> = {}
    allRatingsSnapshot.forEach(doc => {
      const data = doc.data()
      const sellerId = data.sellerId
      if (!ratingsBySeller[sellerId]) {
        ratingsBySeller[sellerId] = []
      }
      ratingsBySeller[sellerId].push(data)
    })
    
    // Get completed requests with accepted offers
    const requestsSnapshot = await getDocs(query(
      collection(db, 'requests'),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end)
    ))
    
    // Get all accepted offers for revenue calculation
    const acceptedOffersQuery = query(
      collection(db, 'offers'),
      where('status', '==', 'accepted'),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end)
    )
    const acceptedOffersSnapshot = await getDocs(acceptedOffersQuery)
    
    // Map requestId -> accepted offer price
    const acceptedOffersMap: Record<string, { sellerId: string; price: number }> = {}
    acceptedOffersSnapshot.forEach(doc => {
      const data = doc.data()
      acceptedOffersMap[data.requestId] = {
        sellerId: data.sellerId,
        price: data.price || 0
      }
    })
    
    // Calculate revenue by seller
    const revenueBySeller: Record<string, number> = {}
    requestsSnapshot.forEach(reqDoc => {
      const reqData = reqDoc.data()
      if (reqData.status === 'completed' || reqData.status === 'received') {
        const offer = acceptedOffersMap[reqDoc.id]
        if (offer) {
          revenueBySeller[offer.sellerId] = (revenueBySeller[offer.sellerId] || 0) + offer.price
        }
      }
    })
    
    // Build seller summaries
    const sellers: SellerSummary[] = []
    
    for (const sellerDoc of sellersSnapshot.docs) {
      const sellerId = sellerDoc.id
      const sellerData = sellerDoc.data()
      const offers = offersBySeller[sellerId] || []
      const ratings = ratingsBySeller[sellerId] || []
      
      // Calculate metrics
      let totalOffers = offers.length
      let acceptedOffers = 0
      let rejectedOffers = 0
      let totalResponseTime = 0
      let responsesCount = 0
      
      offers.forEach(offer => {
        if (offer.status === 'accepted') acceptedOffers++
        else if (offer.status === 'rejected') rejectedOffers++
        
        if (offer.createdAt && offer.updatedAt && offer.status !== 'pending') {
          const createdAt = offer.createdAt?.toMillis ? offer.createdAt.toMillis() : offer.createdAt
          const updatedAt = offer.updatedAt?.toMillis ? offer.updatedAt.toMillis() : offer.updatedAt
          if (createdAt && updatedAt) {
            const responseTime = (updatedAt - createdAt) / (1000 * 60)
            totalResponseTime += responseTime
            responsesCount++
          }
        }
      })
      
      const averageResponseTime = responsesCount > 0 ? totalResponseTime / responsesCount : 0
      const acceptanceRate = totalOffers > 0 ? (acceptedOffers / totalOffers) * 100 : 0
      
      // Calculate average rating
      let totalRatingValue = 0
      ratings.forEach(rating => {
        totalRatingValue += rating.rating || 0
      })
      const averageRating = ratings.length > 0 ? totalRatingValue / ratings.length : 0
      
      const totalRevenue = revenueBySeller[sellerId] || 0
      
      // Simple performance score
      const performanceScore = Math.min(100, Math.round(
        (acceptanceRate * 0.4) +
        (averageRating * 20 * 0.3) +
        (Math.max(0, 100 - (averageResponseTime / 60) * 100) * 0.3)
      ))
      
      sellers.push({
        sellerId,
        sellerName: sellerData.name || 'Unknown',
        totalOffers,
        acceptanceRate,
        averageResponseTime,
        averageRating,
        totalRevenue,
        status: getSellerStatus(performanceScore)
      })
    }
    
    return sellers
  } catch (error) {
    console.error('Error getting all sellers performance:', error)
    throw error
  }
}

/**
 * Get seller ratings
 */
export async function getSellerRatings(sellerId: string, limitCount: number = 10) {
  try {
    const ratingsQuery = query(
      collection(db, 'ratings'),
      where('sellerId', '==', sellerId),
      orderBy('createdAt', 'desc'),
      firestoreLimit(limitCount)
    )
    const ratingsSnapshot = await getDocs(ratingsQuery)
    
    return ratingsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate()
    }))
  } catch (error) {
    console.error('Error getting seller ratings:', error)
    throw error
  }
}

/**
 * Get seller response rate
 */
export async function getSellerResponseRate(
  sellerId: string, 
  startDate: Date, 
  endDate: Date
): Promise<number> {
  try {
    const start = Timestamp.fromDate(startDate)
    const end = Timestamp.fromDate(endDate)
    
    const offersQuery = query(
      collection(db, 'offers'),
      where('sellerId', '==', sellerId),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end)
    )
    const offersSnapshot = await getDocs(offersQuery)
    
    let totalRequests = 0
    let responded = 0
    
    offersSnapshot.forEach(doc => {
      totalRequests++
      if (doc.data().status !== 'pending') {
        responded++
      }
    })
    
    return totalRequests > 0 ? (responded / totalRequests) * 100 : 0
  } catch (error) {
    console.error('Error getting seller response rate:', error)
    throw error
  }
}

/**
 * Get seller offer acceptance rate
 */
export async function getSellerOfferAcceptanceRate(
  sellerId: string, 
  startDate: Date, 
  endDate: Date
): Promise<number> {
  try {
    const performance = await getSellerPerformance(sellerId, startDate, endDate)
    return performance.acceptanceRate
  } catch (error) {
    console.error('Error getting seller offer acceptance rate:', error)
    throw error
  }
}

/**
 * Get seller lost deals
 */
export async function getSellerLostDeals(
  sellerId: string, 
  startDate: Date, 
  endDate: Date
) {
  try {
    const start = Timestamp.fromDate(startDate)
    const end = Timestamp.fromDate(endDate)
    
    const lostDealsQuery = query(
      collection(db, 'lost_deals'),
      where('sellerId', '==', sellerId),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end),
      orderBy('createdAt', 'desc')
    )
    const lostDealsSnapshot = await getDocs(lostDealsQuery)
    
    return lostDealsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate()
    }))
  } catch (error) {
    console.error('Error getting seller lost deals:', error)
    throw error
  }
}

/**
 * Get seller completed orders
 */
export async function getSellerCompletedOrders(
  sellerId: string, 
  startDate: Date, 
  endDate: Date
) {
  try {
    const start = Timestamp.fromDate(startDate)
    const end = Timestamp.fromDate(endDate)
    
    // Get all requests in period
    const requestsQuery = query(
      collection(db, 'requests'),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end)
    )
    const requestsSnapshot = await getDocs(requestsQuery)
    
    const completedOrders = []
    
    for (const reqDoc of requestsSnapshot.docs) {
      const reqData = reqDoc.data()
      
      // Only include completed/received orders
      if (reqData.status === 'completed' || reqData.status === 'received') {
        // Check if this request has an accepted offer from this seller
        const offerQuery = query(
          collection(db, 'offers'),
          where('requestId', '==', reqDoc.id),
          where('sellerId', '==', sellerId),
          where('status', '==', 'accepted')
        )
        const offerSnapshot = await getDocs(offerQuery)
        
        if (!offerSnapshot.empty) {
          completedOrders.push({
            requestId: reqDoc.id,
            ...reqData,
            offerPrice: offerSnapshot.docs[0].data().price || 0,
            createdAt: reqData.createdAt?.toDate()
          })
        }
      }
    }
    
    return completedOrders
  } catch (error) {
    console.error('Error getting seller completed orders:', error)
    throw error
  }
}

/**
 * Calculate seller performance score (0-100)
 */
function calculatePerformanceScore(params: {
  acceptanceRate: number
  completionRate: number
  averageRating: number
  averageResponseTime: number
  lostDealsCount: number
}): number {
  const {
    acceptanceRate,
    completionRate,
    averageRating,
    averageResponseTime,
    lostDealsCount
  } = params

  // Weights for each factor
  const weights = {
    acceptance: 0.25,
    completion: 0.25,
    rating: 0.25,
    response: 0.15,
    lostDeals: 0.10
  }

  // Normalize acceptance rate (0-100)
  const acceptanceScore = Math.min(acceptanceRate, 100)

  // Normalize completion rate (0-100)
  const completionScore = Math.min(completionRate, 100)

  // Normalize rating (0-5 to 0-100)
  const ratingScore = (averageRating / 5) * 100

  // Normalize response time (faster is better, max 60 minutes)
  const responseScore = Math.max(0, 100 - (averageResponseTime / 60) * 100)

  // Normalize lost deals (fewer is better, penalize after 5)
  const lostDealsScore = Math.max(0, 100 - (lostDealsCount * 10))

  // Calculate weighted score
  const score = 
    (acceptanceScore * weights.acceptance) +
    (completionScore * weights.completion) +
    (ratingScore * weights.rating) +
    (responseScore * weights.response) +
    (lostDealsScore * weights.lostDeals)

  return Math.round(score)
}

/**
 * Get seller status based on performance score
 */
function getSellerStatus(score: number): 'excellent' | 'good' | 'average' | 'poor' {
  if (score >= 80) return 'excellent'
  if (score >= 60) return 'good'
  if (score >= 40) return 'average'
  return 'poor'
}

/**
 * Get time-series data for charts
 */
export async function getSellerTimeSeriesData(
  sellerId: string,
  startDate: Date,
  endDate: Date,
  interval: 'daily' | 'weekly' | 'monthly' = 'daily'
) {
  try {
    const start = Timestamp.fromDate(startDate)
    const end = Timestamp.fromDate(endDate)
    
    const offersQuery = query(
      collection(db, 'offers'),
      where('sellerId', '==', sellerId),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end),
      orderBy('createdAt', 'asc')
    )
    const offersSnapshot = await getDocs(offersQuery)
    
    // Group by time interval
    const dataPoints: Record<string, { sent: number; accepted: number; rejected: number }> = {}
    
    offersSnapshot.forEach(doc => {
      const data = doc.data()
      const date = data.createdAt?.toDate()
      if (!date) return
      
      const key = formatDateByInterval(date, interval)
      
      if (!dataPoints[key]) {
        dataPoints[key] = { sent: 0, accepted: 0, rejected: 0 }
      }
      
      dataPoints[key].sent++
      if (data.status === 'accepted') dataPoints[key].accepted++
      if (data.status === 'rejected') dataPoints[key].rejected++
    })
    
    return Object.entries(dataPoints).map(([date, data]) => ({
      date,
      ...data
    }))
  } catch (error) {
    console.error('Error getting time series data:', error)
    throw error
  }
}

/**
 * Format date by interval
 */
function formatDateByInterval(date: Date, interval: 'daily' | 'weekly' | 'monthly'): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  
  switch (interval) {
    case 'daily':
      return `${year}-${month}-${day}`
    case 'weekly':
      const weekNum = getWeekNumber(date)
      return `${year}-W${weekNum}`
    case 'monthly':
      return `${year}-${month}`
    default:
      return `${year}-${month}-${day}`
  }
}

/**
 * Get week number
 */
function getWeekNumber(date: Date): string {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  const dayNum = d.getUTCDay() || 7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum)
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
  const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7)
  return String(weekNo).padStart(2, '0')
}

